import { useEffect, useMemo } from 'react';

import type { DocumentDraft } from './documentDraftTypes';
import {
  useDocumentPdfPreviewQuery,
  type DocumentPdfPreviewMode,
  type DocumentPdfPreviewRefreshMode,
} from './useDocumentPdfPreviewQuery';

interface DocumentPdfPreviewOptions {
  mode?: DocumentPdfPreviewMode;
  refresh?: DocumentPdfPreviewRefreshMode;
}

export function useDocumentPdfPreview(
  patientId: string,
  draft: DocumentDraft | null,
  options: DocumentPdfPreviewOptions = {}
) {
  const mode = options.mode ?? 'visible';
  const previewQuery = useDocumentPdfPreviewQuery(patientId, draft, options);

  const previewUrl = useMemo(() => {
    if (!(previewQuery.data instanceof Blob)) {
      return null;
    }
    return URL.createObjectURL(previewQuery.data);
  }, [previewQuery.data]);

  useEffect(() => {
    if (previewUrl === null) {
      return;
    }
    return () => {
      URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  return {
    previewUrl,
    previewState:
      mode === 'hidden'
        ? ('idle' as const)
        : previewQuery.isPending
          ? ('loading' as const)
          : previewQuery.isError
            ? ('error' as const)
            : ('idle' as const),
  };
}
